import React, { useEffect, useMemo, useState } from 'react';
import { X, Mail, Phone, MapPin, Globe, Tag, Loader2, User2, Shield } from 'lucide-react';
import FeatureShowcase from '../FeatureShowcase';
import { TraderFeatures, traderService } from '../../services/traderService';

type BusinessType = 'trader' | 'crafty' | 'hospitality' | 'professional';

interface AdminBusinessPreviewModalProps {
  businessType: BusinessType;
  business: any;
  onClose: () => void;
  contextLabel?: string;
}

const typeLabels: Record<BusinessType, string> = {
  trader: 'Trader',
  crafty: 'Craftie',
  hospitality: 'Hospitality Business',
  professional: 'Professional Service',
};

const AdminBusinessPreviewModal: React.FC<AdminBusinessPreviewModalProps> = ({
  businessType,
  business,
  onClose,
  contextLabel,
}) => {
  const [features, setFeatures] = useState<TraderFeatures | null>(null);
  const [loadingFeatures, setLoadingFeatures] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadFeatures = async () => {
      if (!business?.user_id) {
        setFeatures(null);
        return;
      }

      setLoadingFeatures(true);
      try {
        const data = await traderService.getTraderFeatures(business.user_id);
        if (isMounted) {
          setFeatures(data);
        }
      } catch (err) {
        console.error('Failed to load features for preview modal:', err);
      } finally {
        if (isMounted) {
          setLoadingFeatures(false);
        }
      }
    };

    loadFeatures();
    return () => {
      isMounted = false;
    };
  }, [business?.user_id]);

  const enabledFeatures = useMemo(() => {
    if (!features) return [];
    const fallbackImage = business?.avatar_url || '/bally-Market-sign-200px.png';
    const raw = [
      {
        title: features.feature1_title || 'Feature 1',
        description: features.feature1_description || '',
        image: features.feature1_image || fallbackImage,
        enabled: features.feature1_enabled,
      },
      {
        title: features.feature2_title || 'Feature 2',
        description: features.feature2_description || '',
        image: features.feature2_image || fallbackImage,
        enabled: features.feature2_enabled,
      },
      {
        title: features.feature3_title || 'Feature 3',
        description: features.feature3_description || '',
        image: features.feature3_image || fallbackImage,
        enabled: features.feature3_enabled,
      },
    ];

    return raw.filter((feature) => {
      const flag = feature.enabled as any;
      return flag === true || flag === 'true' || flag === '1' || flag === 1;
    });
  }, [features, business?.avatar_url]);

  const featureSectionTitle =
    features?.features_section_title?.trim() ||
    (businessType === 'crafty' ? 'Our Crafts' : 'Featured Offerings');

  const featureLayout =
    features?.features_layout === 'columns' ? 'columns' : 'rows';

  const detailEntries = [
    {
      icon: <User2 className="w-4 h-4 text-gray-400" />,
      label: 'Owner',
      value: business?.owner_name || '—',
    },
    {
      icon: <Mail className="w-4 h-4 text-gray-400" />,
      label: 'Email',
      value: business?.email,
      href: business?.email ? `mailto:${business.email}` : undefined,
    },
    {
      icon: <Phone className="w-4 h-4 text-gray-400" />,
      label: 'Phone',
      value: business?.phone_number,
      href: business?.phone_number ? `tel:${business.phone_number}` : undefined,
    },
    {
      icon: <Globe className="w-4 h-4 text-gray-400" />,
      label: 'Website',
      value: business?.website,
      href:
        business?.website && business.website.startsWith('http')
          ? business.website
          : business?.website
          ? `https://${business.website}`
          : undefined,
      external: true,
    },
    {
      icon: <MapPin className="w-4 h-4 text-gray-400" />,
      label: 'Location',
      value: business?.location || '—',
    },
  ];

  const keywords: string[] = business?.keywords || [];

  const professionLabel =
    contextLabel ||
    business?.profession_label ||
    business?.professions?.name ||
    business?.professionals_categories?.name ||
    business?.hospitality_category?.name ||
    '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white w-full max-w-4xl max-h-[90vh] rounded-lg shadow-xl flex flex-col">
        <div className="flex items-start justify-between border-b border-gray-100 px-6 py-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-gray-400">
              {typeLabels[businessType]}
              {professionLabel ? ` · ${professionLabel}` : ''}
            </p>
            <h2 className="text-2xl font-semibold text-gray-900 mt-1">
              {business?.store_name || 'Untitled'}
            </h2>
            {business?.mockup && (
              <span className="inline-flex items-center text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full mt-2">
                <Shield className="w-3 h-3 mr-1" />
                Mockup / Unclaimed
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="Close preview"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="w-full aspect-square rounded-lg overflow-hidden bg-gray-100">
                {business?.avatar_url ? (
                  <img
                    src={business.avatar_url}
                    alt={business?.store_name}
                    className="w-full h-full object-cover object-center"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-gray-400 text-sm">
                    No image
                  </div>
                )}
              </div>

              {keywords.length > 0 && (
                <div className="border border-gray-100 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Tag className="w-4 h-4 text-gray-400" />
                    Keywords
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {keywords.slice(0, 12).map((keyword) => (
                      <span
                        key={keyword}
                        className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded-full"
                      >
                        {keyword}
                      </span>
                    ))}
                    {keywords.length > 12 && (
                      <span className="px-2 py-0.5 text-xs text-gray-500">
                        +{keywords.length - 12} more
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="border border-gray-100 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  Contact Details
                </h3>
                <dl className="space-y-2">
                  {detailEntries.map((entry) => (
                    <div key={entry.label} className="flex items-start text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        {entry.icon}
                        <span className="font-medium">{entry.label}:</span>
                      </div>
                      <div className="ml-2 text-gray-700">
                        {entry.value ? (
                          entry.href ? (
                            <a
                              href={entry.href}
                              target={entry.external ? '_blank' : undefined}
                              rel={entry.external ? 'noreferrer' : undefined}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              {entry.value}
                            </a>
                          ) : (
                            entry.value
                          )
                        ) : (
                          <span className="text-gray-400">Not provided</span>
                        )}
                      </div>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="border border-gray-100 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">
                  About
                </h3>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {business?.description || 'No description available.'}
                </p>
              </div>
            </div>
          </div>

          {loadingFeatures ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 border border-dashed border-gray-200 rounded-lg p-4">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading highlights…
            </div>
          ) : (
            enabledFeatures.length > 0 && (
              <div className="border border-gray-100 rounded-lg p-4">
                <FeatureShowcase
                  title={featureSectionTitle}
                  features={enabledFeatures}
                  layout={featureLayout}
                />
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminBusinessPreviewModal;
